/** Base path for the Soccer app within Daily Sports Life */
import { getShareOrigin } from './canonicalOrigin';

export const SOCCER_BASE = '/soccer';

/** Base path for the Running clubs app */
export const RUNNING_BASE = '/running';

/** Base path for the Hiking clubs app */
export const HIKING_BASE = '/hiking';

/**
 * Build an in-app path under /soccer (e.g. soccerPath('/clubs') → '/soccer/clubs').
 */
export function soccerPath(path = '') {
  if (!path || path === '/') {
    return SOCCER_BASE;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === SOCCER_BASE || normalized.startsWith(`${SOCCER_BASE}/`)) {
    return normalized;
  }
  return `${SOCCER_BASE}${normalized}`;
}

/**
 * Build an in-app path under /running.
 */
export function runningPath(path = '') {
  if (!path || path === '/') {
    return RUNNING_BASE;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === RUNNING_BASE || normalized.startsWith(`${RUNNING_BASE}/`)) {
    return normalized;
  }
  return `${RUNNING_BASE}${normalized}`;
}

/**
 * Build an in-app path under /hiking.
 */
export function hikingPath(path = '') {
  if (!path || path === '/') {
    return HIKING_BASE;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === HIKING_BASE || normalized.startsWith(`${HIKING_BASE}/`)) {
    return normalized;
  }
  return `${HIKING_BASE}${normalized}`;
}

/** Full URL for sharing (WhatsApp, copy link, etc.) */
export function soccerUrl(path = '') {
  if (typeof window === 'undefined') {
    return soccerPath(path);
  }
  return `${getShareOrigin()}${soccerPath(path)}`;
}

/** Full URL for running practice / route sharing */
export function runningUrl(path = '') {
  if (typeof window === 'undefined') {
    return runningPath(path);
  }
  return `${getShareOrigin()}${runningPath(path)}`;
}

/** Full URL for hiking outing / trail sharing */
export function hikingUrl(path = '') {
  if (typeof window === 'undefined') {
    return hikingPath(path);
  }
  return `${getShareOrigin()}${hikingPath(path)}`;
}

/** Unified signup URL at site root (optionally pre-select a club). */
export function registerUrl(clubId = null) {
  const query = clubId ? `?clubId=${encodeURIComponent(clubId)}` : '';
  const path = `/register${query}`;
  if (typeof window === 'undefined') {
    return path;
  }
  return `${getShareOrigin()}${path}`;
}

/** Shared account profile (name, photo, phone) for all sports. */
export function accountPath(path = '') {
  if (!path || path === '/') {
    return '/account';
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return normalized === '/account' || normalized.startsWith('/account/')
    ? normalized
    : `/account${normalized}`;
}

/** Logged-in users finish soccer player details here (not new account signup). */
export function completeProfilePath(clubId = null) {
  const query = clubId ? `?clubId=${encodeURIComponent(clubId)}` : '';
  return soccerPath(`/players/complete-profile${query}`);
}

/** Logged-in users finish running profile here. */
export function runningCompleteProfilePath(clubId = null) {
  const query = clubId ? `?clubId=${encodeURIComponent(clubId)}` : '';
  return runningPath(`/profile/complete${query}`);
}

/** Logged-in users finish hiker profile here. */
export function hikingCompleteProfilePath(clubId = null) {
  const query = clubId ? `?clubId=${encodeURIComponent(clubId)}` : '';
  return hikingPath(`/profile/complete${query}`);
}

/** Sport-aware complete profile path for club join flows. */
export function completeProfilePathForSport(sport, clubId = null) {
  const normalized = String(sport || '').toUpperCase();
  if (normalized === 'RUNNING' || normalized === 'CROSS_COUNTRY' || normalized === 'XC') {
    return runningCompleteProfilePath(clubId);
  }
  if (normalized === 'HIKING') {
    return hikingCompleteProfilePath(clubId);
  }
  return completeProfilePath(clubId);
}

/** Session key for post-login return URL (legacy name kept for compatibility) */
export const AUTH_REDIRECT_KEY = 'votingRedirectUrl';

/** Base path for the Training app */
export const TRAINING_BASE = '/training';

/**
 * Build an in-app path under /training (e.g. trainingPath('/soccer') → '/training/soccer').
 */
export function trainingPath(path = '') {
  if (!path || path === '/') {
    return TRAINING_BASE;
  }
  const normalized = path.startsWith('/') ? path : `/${path}`;
  if (normalized === TRAINING_BASE || normalized.startsWith(`${TRAINING_BASE}/`)) {
    return normalized;
  }
  return `${TRAINING_BASE}${normalized}`;
}

/** Soccer training tracker (daily log / plans) */
export function soccerTrainingTrackerPath() {
  return trainingPath('/soccer/tracker');
}

/**
 * Normalize post-login redirects.
 * Keep portal / training / blog absolute paths; prefix legacy soccer-relative paths with /soccer.
 */
export function normalizeSoccerRedirect(url) {
  if (!url) {
    return soccerPath('/');
  }
  if (
    url.startsWith(SOCCER_BASE) ||
    url.startsWith(RUNNING_BASE) ||
    url.startsWith(HIKING_BASE) ||
    url.startsWith(TRAINING_BASE) ||
    url.startsWith('/account') ||
    url.startsWith('/blog') ||
    url.startsWith('/login') ||
    url.startsWith('/register') ||
    url === '/'
  ) {
    return url;
  }
  if (url.startsWith('/')) {
    return soccerPath(url);
  }
  return url;
}

export function storeAuthRedirect(url) {
  if (!url || typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(AUTH_REDIRECT_KEY, normalizeSoccerRedirect(url));
}

export function peekAuthRedirect() {
  if (typeof window === 'undefined') {
    return null;
  }
  return sessionStorage.getItem(AUTH_REDIRECT_KEY);
}

export function consumeAuthRedirect() {
  const url = peekAuthRedirect();
  if (url) {
    sessionStorage.removeItem(AUTH_REDIRECT_KEY);
  }
  return url;
}

export function getLiveScrimmagePath(practiceId) {
  return soccerPath(`/practices/${practiceId}/scrimmage-stats`);
}

export function storeReturnPathFromLocation(pathname, search = '') {
  if (!pathname || pathname.includes('/login') || pathname.includes('/register')) {
    return;
  }
  // Public scrimmage share pages set the live recording URL once practiceId is loaded.
  if (pathname.includes('/public/scrimmage-stats/')) {
    return;
  }
  storeAuthRedirect(`${pathname}${search || ''}`);
}

export function isSoccerPath(pathname) {
  return pathname === SOCCER_BASE || pathname.startsWith(`${SOCCER_BASE}/`);
}

export function isRunningPath(pathname) {
  return pathname === RUNNING_BASE || pathname.startsWith(`${RUNNING_BASE}/`);
}

export function isHikingPath(pathname) {
  return pathname === HIKING_BASE || pathname.startsWith(`${HIKING_BASE}/`);
}

/** Club home path based on sport (RUNNING clubs live under /running, HIKING under /hiking). */
export function clubHomePath(clubId, sport) {
  const normalized = String(sport || '').toUpperCase();
  if (normalized === 'RUNNING' || normalized === 'CROSS_COUNTRY' || normalized === 'XC') {
    return runningPath(`/clubs/${clubId}`);
  }
  if (normalized === 'HIKING') {
    return hikingPath(`/clubs/${clubId}`);
  }
  return soccerPath(`/clubs/${clubId}`);
}
